import { KeyRound } from 'lucide-react';
import { useState } from 'react';
import { usePublisher } from '../../hooks/usePublisher';
import {
  API_KEY_MANAGEMENT_PERMISSION_MESSAGE,
  getApiKeyManagementPermissionMessage,
} from '../../utils/apiKeyAccess';
import {
  ApiKeyManagementPanel,
  ApiKeysSkeleton,
  type PublisherScope,
} from '../PreferencesSettingsPage/ApiKeyManagementPanel';
import { SettingsNotice, SettingsPage, SettingsRailCard } from '../settings';

/** The API reads this header in `api/src/trpc/context.ts`. */
const API_KEY_HEADER = 'x-api-key';

export const DeveloperApiKeysPage = () => {
  const { activePublisher, isLoading } = usePublisher();
  // Set when a request comes back without ManageTeam mid-session; the page
  // explains it inline instead of redirecting to Storage. Scoped to the
  // publisher it came from so switching publishers clears it.
  const [deniedPublisherId, setDeniedPublisherId] = useState<string | null>(
    null,
  );
  const permissionDenied =
    deniedPublisherId !== null &&
    deniedPublisherId === activePublisher?.publisherId;
  const publisherScope: PublisherScope | null = !activePublisher
    ? null
    : {
        publisherId: activePublisher.publisherId,
        publisherType:
          activePublisher.publisherType === 'Organization'
            ? 'organization'
            : 'individual',
      };

  const accessError = publisherScope
    ? getApiKeyManagementPermissionMessage(activePublisher?.canManageTeam)
    : null;

  const rail = (
    <SettingsRailCard title="Using a key">
      <p>
        Send the secret in the{' '}
        <code className="rounded-md bg-seeker-800 px-1.5 py-0.5 font-mono text-xs text-seeker-200">
          {API_KEY_HEADER}
        </code>{' '}
        request header.
      </p>
      <ul>
        <li>New keys carry release-management and store-submission access.</li>
        <li>The secret is shown once, right after you create the key.</li>
        <li>Revoking a key takes effect immediately.</li>
      </ul>
    </SettingsRailCard>
  );

  function renderBody() {
    if (isLoading) {
      return <ApiKeysSkeleton />;
    }

    if (accessError || permissionDenied) {
      return (
        <SettingsNotice
          className="motion-enter"
          tone="warning"
          title="Team management permission required"
          description={accessError ?? API_KEY_MANAGEMENT_PERMISSION_MESSAGE}
        />
      );
    }

    if (!publisherScope) {
      return (
        <SettingsNotice
          className="motion-enter"
          tone="warning"
          title="No active publisher"
          description="You need an active publisher before you can manage API keys."
        />
      );
    }

    return (
      <ApiKeyManagementPanel
        publisherScope={publisherScope}
        onPermissionDenied={() =>
          setDeniedPublisherId(activePublisher?.publisherId ?? null)
        }
      />
    );
  }

  return (
    <SettingsPage
      icon={<KeyRound />}
      title="API Keys"
      description="Manage API keys for the publishing CLI. New keys are created with release-management and store-submission access, and the secret is shown only once."
      rail={rail}
    >
      {renderBody()}
    </SettingsPage>
  );
};
